import * as Dialog from '@radix-ui/react-dialog';
import { AlertCircle, X } from 'lucide-react';

import { Button } from '@/components/ui/button';

interface DeleteCustomToolDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  toolName: string;
  configurations: Array<{ id: string; name: string }>;
  isLoading?: boolean;
}

export const DeleteCustomToolDialog = ({
  open,
  onOpenChange,
  onConfirm,
  toolName,
  configurations,
  isLoading = false,
}: DeleteCustomToolDialogProps) => {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/60" />
        <Dialog.Content className="bg-background fixed top-1/2 left-1/2 z-50 w-full max-w-lg -translate-x-1/2 -translate-y-1/2 rounded-lg p-6 shadow-lg">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-2">
              <AlertCircle className="text-destructive h-5 w-5" />
              <Dialog.Title className="text-lg font-semibold">Delete Custom Tool?</Dialog.Title>
            </div>
            <Dialog.Close asChild>
              <Button variant="ghost" size="icon" disabled={isLoading}>
                <X className="h-4 w-4" />
              </Button>
            </Dialog.Close>
          </div>

          <div className="mt-4 space-y-3">
            <p className="text-muted-foreground text-sm">
              Are you sure you want to delete <strong>&quot;{toolName}&quot;</strong>?
            </p>

            {configurations.length > 0 && (
              <>
                <div className="border-destructive/20 bg-destructive/5 rounded-md border p-3">
                  <p className="text-destructive text-sm font-medium">
                    This tool is currently used in {configurations.length}{' '}
                    {configurations.length === 1 ? 'configuration' : 'configurations'}:
                  </p>
                  <ul className="text-muted-foreground mt-2 max-h-32 space-y-1 overflow-y-auto text-sm">
                    {configurations.map((config) => (
                      <li key={config.id} className="flex items-center gap-2">
                        <span className="text-muted-foreground">•</span>
                        <span>{config.name}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <p className="text-destructive text-sm font-medium">
                  Deleting this tool will automatically remove it from all configurations listed
                  above. This action cannot be undone.
                </p>
              </>
            )}

            {configurations.length === 0 && (
              <p className="text-muted-foreground text-sm">
                This tool is not currently used in any configuration. This action cannot be undone.
              </p>
            )}
          </div>

          <div className="mt-6 flex justify-end gap-3">
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isLoading}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={onConfirm}
              disabled={isLoading}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isLoading ? 'Deleting...' : 'Delete Tool'}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
